import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { interviewsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Video, Users, HelpCircle, ChevronRight, Briefcase, Send } from 'lucide-react';
import SendToCandidateDialog from '../components/SendToCandidateDialog';
import BulkSendDialog from '../components/BulkSendDialog';
import LowCreditBanner from '../components/LowCreditBanner';

const statusStyle = (status) => {
  const map = {
    active: { bg: 'rgba(16,185,129,0.12)', color: '#059669', border: 'rgba(16,185,129,0.3)' },
    draft: { bg: 'rgba(100,116,139,0.12)', color: '#64748B', border: 'rgba(100,116,139,0.3)' },
    closed: { bg: 'rgba(220,38,38,0.1)', color: '#DC2626', border: 'rgba(220,38,38,0.3)' },
  };
  return map[status] || map.draft;
};

export default function InterviewsGlobalPage({ embedded = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendTarget, setSendTarget] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const navigate = useNavigate();

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const allSelected = items.length > 0 && selected.size === items.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((i) => i._id)));

  const handleBulkSend = async (recipients) => {
    const targets = items.filter((i) => selected.has(i._id));
    const tasks = [];
    targets.forEach((t) => recipients.forEach((r) => tasks.push(
      interviewsAPI.sendToCandidate(t.job_id, { ...r, template: t.template || 'technical' })
    )));
    const results = await Promise.allSettled(tasks);
    const failed = results.filter((r) => r.status === 'rejected').length;
    setSelected(new Set());
    return { sent: results.length - failed, failed };
  };

  const load = useCallback(async () => {
    try {
      const res = await interviewsAPI.listAll();
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load interviews');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSend = async ({ candidate_name, candidate_email }) => {
    await interviewsAPI.sendToCandidate(sendTarget.job_id, {
      candidate_name, candidate_email, template: sendTarget.template || 'technical',
    });
    toast.success(`Interview sent to ${candidate_name || candidate_email}`);
  };

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#64748B' }} data-testid="interviews-loading">Loading interviews...</div>;
  }

  return (
    <div style={embedded ? undefined : { padding: '2rem', maxWidth: 1200, margin: '0 auto' }} data-testid="interviews-global-page">
      {!embedded && <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#002A38', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <Video size={26} color="#F2600C" /> AI Interviews
        </h1>
        <p style={{ color: '#64748B', fontSize: '0.9375rem', marginTop: '0.25rem' }} data-testid="interviews-count">
          {items.length} interview{items.length === 1 ? '' : 's'} across all your jobs
        </p>
      </div>}

      {!embedded && <LowCreditBanner />}

      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem 1rem', background: '#FAF7F2', borderRadius: 16, border: '1px dashed #CBD5E1' }} data-testid="interviews-empty">
          <Video size={40} color="#94A3B8" style={{ margin: '0 auto 1rem' }} />
          <div style={{ fontWeight: 700, color: '#334155', fontSize: '1.0625rem' }}>No interviews yet</div>
          <p style={{ color: '#64748B', fontSize: '0.875rem', marginTop: '0.5rem', maxWidth: 420, margin: '0.5rem auto 1.25rem' }}>
            AI interviews are created from inside a job. Open a job and use “Create Interview” to set one up.
          </p>
          <button onClick={() => navigate('/jobs')} data-testid="interviews-go-to-jobs" style={{ padding: '0.625rem 1.25rem', borderRadius: 999, background: '#F2600C', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
            Go to Jobs
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {/* Bulk action bar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.625rem 1rem', background: selected.size ? 'rgba(124,58,237,0.06)' : '#FAF7F2', border: `1px solid ${selected.size ? 'rgba(124,58,237,0.25)' : '#ECE7DE'}`, borderRadius: 12 }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', color: '#475569', cursor: 'pointer', fontWeight: 600 }}>
              <input data-testid="interviews-select-all" type="checkbox" checked={allSelected} onChange={toggleAll} style={{ width: 16, height: 16, cursor: 'pointer' }} />
              {selected.size ? `${selected.size} selected` : 'Select all'}
            </label>
            <button
              data-testid="interviews-bulk-send-btn"
              onClick={() => setBulkOpen(true)}
              disabled={selected.size === 0}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 1rem', borderRadius: 999, background: selected.size ? '#F2600C' : '#ECE7DE', color: selected.size ? '#fff' : '#94A3B8', border: 'none', fontSize: '0.8125rem', fontWeight: 600, cursor: selected.size ? 'pointer' : 'default' }}
            >
              <Send size={14} /> Bulk Send
            </button>
          </div>
          {items.map((i) => {
            const st = statusStyle(i.status);
            const isSel = selected.has(i._id);
            return (
              <div
                key={i._id}
                data-testid={`interview-row-${i._id}`}
                onClick={() => navigate(`/interviews/${i._id}/results`)}
                style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.125rem 1.25rem', background: '#fff', border: `1px solid ${isSel ? '#F2600C' : '#ECE7DE'}`, borderRadius: 14, cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#F2600C'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(124,58,237,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = isSel ? '#F2600C' : '#ECE7DE'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <input
                  data-testid={`interview-select-${i._id}`}
                  type="checkbox"
                  checked={isSel}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggle(i._id)}
                  style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
                />
                <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(124,58,237,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Video size={20} color="#F2600C" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: '#002A38', fontSize: '0.9375rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {i.template_name || i.template || 'AI Interview'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#64748B', fontSize: '0.8125rem', marginTop: '0.1875rem' }}>
                    <Briefcase size={13} /> {i.job_title || 'Untitled job'}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexShrink: 0 }}>
                  <div style={{ textAlign: 'center', color: '#475569' }} title="Questions">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontWeight: 700, fontSize: '0.9375rem' }}><HelpCircle size={14} color="#94A3B8" />{i.question_count ?? (i.questions ? i.questions.length : 0)}</div>
                    <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>Questions</div>
                  </div>
                  <div style={{ textAlign: 'center', color: '#475569' }} title="Completed / Total sessions">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontWeight: 700, fontSize: '0.9375rem' }}><Users size={14} color="#94A3B8" />{i.completed_count ?? 0}/{i.session_count ?? 0}</div>
                    <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>Sessions</div>
                  </div>
                  <button
                    data-testid={`send-interview-${i._id}`}
                    onClick={(e) => { e.stopPropagation(); setSendTarget(i); }}
                    title="Send to candidate"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.4rem 0.75rem', borderRadius: 999, background: 'rgba(124,58,237,0.1)', color: '#F2600C', border: '1px solid rgba(124,58,237,0.25)', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                  >
                    <Send size={13} /> Send
                  </button>
                  <span style={{ padding: '0.25rem 0.75rem', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize', background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                    {i.status || 'draft'}
                  </span>
                  <ChevronRight size={18} color="#CBD5E1" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <SendToCandidateDialog
        open={!!sendTarget}
        onClose={() => setSendTarget(null)}
        onSend={handleSend}
        title={`Send interview — ${sendTarget?.job_title || ''}`}
        accent="#F2600C"
      />

      <BulkSendDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        itemCount={selected.size}
        onSendAll={handleBulkSend}
        title="Bulk send interviews"
        accent="#F2600C"
      />
    </div>
  );
}
